import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { buscarIniciativasPaginadas } from 'services/iniciativa.service';
import { Header } from '../HomePublic/components/Header';
import { HeroIniciativas } from '../Iniciativas/components/HeroIniciativas';
import { FiltrosIniciativas } from '../Iniciativas/components/FiltrosIniciativas';
import { IniciativaCard } from '../HomePublic/components/IniciativaCard';
import { CategoriaPremioId } from 'utils/iniciativa-categoria';
import '../Iniciativas/style.scss';

export function ClassificadasEtapa1() {
  const iniciativasPorPagina = 20;
  const { user } = useAuth();
  const location = useLocation();
  const [categoriaSelecionada, setCategoriaSelecionada] = useState(
    location.state?.categoria || 'todos'
  );
  const [termoBusca, setTermoBusca] = useState('');
  const [debouncedTermo, setDebouncedTermo] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [iniciativas, setIniciativas] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedTermo(termoBusca.trim());
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [termoBusca]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [categoriaSelecionada, debouncedTermo]);

  useEffect(() => {
    async function carregarIniciativas() {
      try {
        setLoading(true);
        const categoria = categoriaSelecionada !== 'todos'
          ? (categoriaSelecionada as CategoriaPremioId)
          : undefined;

        const { items, total } = await buscarIniciativasPaginadas({
          userId: user?._id,
          page: paginaAtual,
          pageSize: iniciativasPorPagina,
          categoria,
          search: debouncedTermo || undefined,
          status: "inscrita",
          classificadaEtapa1: true,
        });

        setIniciativas(items);
        setTotalItems(total);
      } catch (error) {
        setIniciativas([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    }

    if (user?._id) {
      carregarIniciativas();
    }
  }, [user?._id, paginaAtual, categoriaSelecionada, debouncedTermo]);

  const totalPaginas = Math.max(1, Math.ceil(totalItems / iniciativasPorPagina));

  const handleBuscar = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setDebouncedTermo(termoBusca.trim());
  };

  const handlePaginaChange = (pagina: number) => {
    setPaginaAtual(pagina);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div id="iniciativas-wrapper">
      <Header currentPage="classificadas-etapa-1" />

      <HeroIniciativas
        categoriaSelecionada={categoriaSelecionada}
        onCategoriaChange={setCategoriaSelecionada}
        title="Classificadas Etapa 1"
        subtitle={<>Conheça as iniciativas classificadas <br />para a Etapa 1 do Prêmio BB 2026</>}
      />

      <FiltrosIniciativas
        categoriaSelecionada=""
        onCategoriaChange={() => { }}
        termoBusca={termoBusca}
        onBuscaChange={setTermoBusca}
        onBuscar={handleBuscar}
      />

      <main className="iniciativas-main">
        <div className="iniciativas-main__container">
          <div className="iniciativas-main__header">
            <h2 className="iniciativas-main__count" tabIndex={0} aria-live="polite">
              {totalItems} iniciativas encontradas
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-8">
              <span className="text-blue-bb">Carregando iniciativas...</span>
            </div>
          ) : iniciativas.length === 0 ? (
            <div className="iniciativas-main__empty" role="status" aria-live="polite">
              <p className="iniciativas-main__empty-title">Nenhuma iniciativa classificada para a Etapa 1 encontrada.</p>
            </div>
          ) : (
            <>
              <div className="iniciativas-main__grid">
                {iniciativas.map((iniciativa: any) => (
                  <IniciativaCard
                    key={iniciativa.id}
                    {...iniciativa}
                    ocultarNaoValidos
                  />
                ))}
              </div>

              {totalPaginas > 1 && (
                <nav className="iniciativas-main__pagination" role="navigation" aria-label="Paginação de iniciativas">
                  <button
                    className="pagination__btn pagination__btn--prev"
                    onClick={() => handlePaginaChange(paginaAtual - 1)}
                    disabled={paginaAtual === 1}
                    aria-label="Página anterior"
                  >
                    ‹
                  </button>

                  <div className="pagination__numbers" role="list">
                    {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((pagina) => (
                      <button
                        key={pagina}
                        className={`pagination__number ${paginaAtual === pagina ? 'active' : ''}`}
                        onClick={() => handlePaginaChange(pagina)}
                        aria-label={`Página ${pagina}`}
                        aria-current={paginaAtual === pagina ? 'page' : undefined}
                        role="listitem"
                      >
                        {pagina}
                      </button>
                    ))}
                  </div>

                  <button
                    className="pagination__btn pagination__btn--next"
                    onClick={() => handlePaginaChange(paginaAtual + 1)}
                    disabled={paginaAtual === totalPaginas}
                    aria-label="Próxima página"
                  >
                    ›
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
